import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Users, CheckCircle, XCircle, LogIn } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamJoinPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, refreshUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error, login_required
  const [message, setMessage] = useState('');
  const [teamName, setTeamName] = useState('');
  
  const invitationToken = searchParams.get('token');

  useEffect(() => {
    if (!invitationToken) {
      setStatus('error');
      setMessage('Geen geldige uitnodigingslink');
      return;
    }

    if (!isAuthenticated) {
      setStatus('login_required');
      setMessage('Log in of registreer om de uitnodiging te accepteren');
      return;
    }

    // User is authenticated, try to join
    joinTeam();
  }, [invitationToken, isAuthenticated, token]);

  const joinTeam = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team/join/${invitationToken}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStatus('success');
        setMessage(data.message);
        setTeamName(data.team_name);
        toast.success('Welkom bij het team!');
        
        // Refresh user data to get new plan
        await refreshUser();
        
        // Redirect to team page after delay
        setTimeout(() => {
          navigate('/team');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(data.detail || 'Kon niet toetreden tot het team');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Er ging iets mis bij het verwerken van de uitnodiging');
    }
  };

  const handleLogin = () => {
    // Store the invitation URL to redirect back after login
    sessionStorage.setItem('redirectAfterLogin', `/team/join?token=${invitationToken}`);
    navigate('/login');
  };

  const handleRegister = () => {
    sessionStorage.setItem('redirectAfterLogin', `/team/join?token=${invitationToken}`);
    navigate('/register');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-card border border-border rounded-2xl p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
              </div>
              <h1 className="text-xl font-bold mb-2">Uitnodiging verwerken...</h1>
              <p className="text-muted">Even geduld alsjeblieft</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-xl font-bold mb-2">Welkom bij het team!</h1>
              <p className="text-muted mb-4">{message}</p>
              {teamName && (
                <div className="bg-accent/10 rounded-xl p-4 mb-4">
                  <Users className="w-8 h-8 text-accent mx-auto mb-2" />
                  <p className="font-semibold">{teamName}</p>
                </div>
              )}
              <p className="text-sm text-muted">Je wordt doorgestuurd naar de teampagina...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-xl font-bold mb-2">Uitnodiging mislukt</h1>
              <p className="text-muted mb-6">{message}</p>
              <button
                onClick={() => navigate('/')}
                className="bg-accent text-white px-6 py-3 rounded-xl font-medium hover:bg-accent/90 transition-colors"
              >
                Terug naar home
              </button>
            </>
          )}

          {status === 'login_required' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center">
                <LogIn className="w-8 h-8 text-accent" />
              </div>
              <h1 className="text-xl font-bold mb-2">Login vereist</h1>
              <p className="text-muted mb-6">{message}</p>
              <div className="space-y-3">
                <button
                  onClick={handleLogin}
                  className="w-full bg-accent text-white px-6 py-3 rounded-xl font-medium hover:bg-accent/90 transition-colors"
                >
                  Inloggen
                </button>
                <button
                  onClick={handleRegister}
                  className="w-full border border-border px-6 py-3 rounded-xl font-medium hover:bg-muted/10 transition-colors"
                >
                  Nieuw account aanmaken
                </button>
              </div>
              <p className="text-sm text-muted mt-4">
                Na het inloggen word je automatisch toegevoegd aan het team.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
